import type {
  RecordedStep,
  RecordingSession,
  SelectorPreference,
  ActionType,
} from './types';
import { getApiKey } from './storage';
import { createRecording, appendRecordingSteps } from './api';

/**
 * Recorder - Captures user interactions and builds action manifests
 */
export class Recorder {
  private session: RecordingSession | null = null;
  private listeners: Array<{ element: EventTarget; type: string; handler: EventListener }> = [];
  private lastUrl: string = '';
  private stepCounter = 0;
  private onStepRecorded?: (step: RecordedStep) => void;
  private unsentSteps: RecordedStep[] = [];
  private syncInterval: number | null = null;
  private isSyncing = false;
  private readonly SYNC_INTERVAL_MS = 5000; // Sync every 5 seconds
  private readonly BATCH_SIZE = 10; // Batch steps before syncing
  private phantomClickEnabled = false; // Toggle for phantom click mode
  private onPhantomModeChange?: (enabled: boolean) => void;

  constructor(onStepRecorded?: (step: RecordedStep) => void, onPhantomModeChange?: (enabled: boolean) => void) {
    this.onStepRecorded = onStepRecorded;
    this.onPhantomModeChange = onPhantomModeChange;
  }

  /**
   * Strip the warpway query parameter from URLs to prevent end users from seeing the SDK
   */
  private stripWarpwayParam(url: string): string {
    try {
      const urlObj = new URL(url);
      urlObj.searchParams.delete('warpway');
      return urlObj.toString();
    } catch (error) {
      console.warn('[Warpway Recorder] Failed to parse URL:', url, error);
      return url;
    }
  }

  /**
   * Start a new recording session
   */
  async startRecording(): Promise<RecordingSession> {
    // Check if API key exists
    const apiKey = getApiKey();
    if (!apiKey) {
      throw new Error('API key not found. Please configure your API key first.');
    }

    // Create local session
    this.session = {
      startedAt: Date.now(),
      steps: [],
      currentUrl: this.stripWarpwayParam(window.location.href),
      pageSignature: this.generatePageSignature(),
    };

    this.lastUrl = this.stripWarpwayParam(window.location.href);
    this.stepCounter = 0;
    this.unsentSteps = [];
    this.isSyncing = false;

    try {
      // Create recording on backend
      const recordingId = await createRecording(apiKey, this.session);
      this.session.id = recordingId;

      console.log('[Warpway Recorder] Recording created on backend:', recordingId);
    } catch (error) {
      console.error('[Warpway Recorder] Failed to create recording on backend:', error);
      throw new Error('Failed to create recording on backend. Please check your API key and try again.');
    }

    // Attach event listeners
    this.attachEventListeners();

    // Start periodic sync
    this.startPeriodicSync();

    return this.session;
  }

  /**
   * Stop recording and detach listeners
   */
  async stopRecording(): Promise<RecordingSession | null> {
    // Stop periodic sync
    this.stopPeriodicSync();

    // Sync any remaining unsent steps
    if (this.unsentSteps.length > 0) {
      await this.syncStepsToBackend();
    }

    this.detachEventListeners();
    const session = this.session;
    this.session = null;
    this.unsentSteps = [];
    return session;
  }

  /**
   * Get current session
   */
  getSession(): RecordingSession | null {
    return this.session;
  }

  /**
   * Attach event listeners for recording
   */
  private attachEventListeners(): void {
    // Click events
    const clickHandler = (e: Event) => this.handleClick(e as MouseEvent);
    document.addEventListener('click', clickHandler, true);
    this.listeners.push({ element: document, type: 'click', handler: clickHandler });

    // Change events (for all form inputs - only fires when value finalizes)
    const changeHandler = (e: Event) => this.handleChange(e as Event);
    document.addEventListener('change', changeHandler, true);
    this.listeners.push({ element: document, type: 'change', handler: changeHandler });

    // Navigation detection (SPA)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = (...args) => {
      originalPushState.apply(history, args);
      this.handleNavigation('spa');
    };

    history.replaceState = (...args) => {
      originalReplaceState.apply(history, args);
      this.handleNavigation('spa');
    };

    // Listen for popstate (back/forward navigation)
    const popstateHandler = () => this.handleNavigation('spa');
    window.addEventListener('popstate', popstateHandler);
    this.listeners.push({ element: window, type: 'popstate', handler: popstateHandler });

    // Hard navigation detection (beforeunload)
    const beforeUnloadHandler = () => this.handleNavigation('hard');
    window.addEventListener('beforeunload', beforeUnloadHandler);
    this.listeners.push({ element: window, type: 'beforeunload', handler: beforeUnloadHandler });
  }

  /**
   * Detach all event listeners
   */
  private detachEventListeners(): void {
    this.listeners.forEach(({ element, type, handler }) => {
      element.removeEventListener(type, handler as EventListener, true);
    });
    this.listeners = [];
  }

  /**
   * Handle click events
   */
  private handleClick(e: MouseEvent): void {
    if (!this.session) return;

    const target = e.target as HTMLElement;

    // Skip clicks on Warpway UI
    if (this.isWarpwayElement(target)) {
      return;
    }

    // Skip clicks on form inputs - they'll be captured as fill actions
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement
    ) {
      return;
    }

    // Check if phantom click mode is enabled
    const isPhantomClick = this.phantomClickEnabled;

    // If phantom click, prevent default action and propagation
    if (isPhantomClick) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      // Show visual feedback
      this.showPhantomClickFeedback(target);

      // Auto-disable phantom mode after recording one click
      this.setPhantomClickMode(false);
    }

    const step: RecordedStep = {
      id: this.generateStepId(),
      type: 'click',
      timestamp: Date.now(),
      selectors: this.generateSelectors(target),
      metadata: {
        tagName: target.tagName.toLowerCase(),
        text: this.getElementText(target),
        attributes: this.getRelevantAttributes(target),
        phantom: isPhantomClick || undefined, // Only include if true
      },
    };

    this.recordStep(step);
  }

  /**
   * Handle change events (all form inputs)
   */
  private handleChange(e: Event): void {
    if (!this.session) return;

    const target = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

    // Skip if not a form element
    if (!target || !('value' in target)) {
      return;
    }

    // Skip Warpway elements
    if (this.isWarpwayElement(target)) {
      return;
    }

    let value: string;
    if (target instanceof HTMLInputElement && target.type === 'checkbox') {
      value = target.checked.toString();
    } else if (target instanceof HTMLInputElement && target.type === 'radio') {
      value = target.value;
    } else {
      value = target.value;
    }

    // Mask sensitive fields (passwords, OTPs, etc.)
    const shouldMask = this.isSensitiveField(target as HTMLElement);
    if (shouldMask) {
      value = '[MASKED]';
    }

    const step: RecordedStep = {
      id: this.generateStepId(),
      type: 'fill',
      timestamp: Date.now(),
      selectors: this.generateSelectors(target as HTMLElement),
      value,
      metadata: {
        tagName: target.tagName.toLowerCase(),
        attributes: this.getRelevantAttributes(target as HTMLElement),
      },
    };

    this.recordStep(step);
  }

  /**
   * Handle navigation events
   */
  private handleNavigation(type: 'spa' | 'hard'): void {
    if (!this.session) return;

    const newUrl = this.stripWarpwayParam(window.location.href);

    // Skip if URL hasn't changed
    if (newUrl === this.lastUrl) return;

    const step: RecordedStep = {
      id: this.generateStepId(),
      type: 'navigate',
      timestamp: Date.now(),
      url: newUrl,
      routePattern: type === 'spa' ? this.extractRoutePattern(newUrl) : undefined,
    };

    this.lastUrl = newUrl;
    this.recordStep(step);
  }

  /**
   * Record a step to the session
   */
  private recordStep(step: RecordedStep): void {
    if (!this.session) return;

    this.session.steps.push(step);
    this.unsentSteps.push(step);

    if (this.onStepRecorded) {
      this.onStepRecorded(step);
    }

    // If batch size is reached, sync immediately
    if (this.unsentSteps.length >= this.BATCH_SIZE) {
      this.syncStepsToBackend().catch((error) => {
        console.error('[Warpway Recorder] Failed to sync steps:', error);
        // Continue recording even if sync fails
      });
    }
  }

  /**
   * Show visual feedback for phantom click
   */
  private showPhantomClickFeedback(element: HTMLElement): void {
    // Create a temporary highlight element
    const rect = element.getBoundingClientRect();
    const highlight = document.createElement('div');
    highlight.className = 'warpway-phantom-click-highlight';
    highlight.style.cssText = `
      position: fixed;
      top: ${rect.top}px;
      left: ${rect.left}px;
      width: ${rect.width}px;
      height: ${rect.height}px;
      border: 3px dashed #10b981;
      background: rgba(16, 185, 129, 0.1);
      border-radius: 4px;
      pointer-events: none;
      z-index: 2147483646;
      animation: warpway-phantom-pulse 0.6s ease-out;
    `;

    // Add animation styles if not already present
    this.injectPhantomClickStyles();

    // Add to DOM
    document.body.appendChild(highlight);

    // Remove after animation
    setTimeout(() => {
      highlight.remove();
    }, 600);

    // Show toast notification
    this.showPhantomClickToast();
  }

  /**
   * Inject phantom click animation styles
   */
  private injectPhantomClickStyles(): void {
    const styleId = 'warpway-phantom-click-styles';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes warpway-phantom-pulse {
        0% {
          transform: scale(1);
          opacity: 1;
        }
        50% {
          transform: scale(1.05);
          opacity: 0.7;
        }
        100% {
          transform: scale(1);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Show toast notification for phantom click
   */
  private showPhantomClickToast(): void {
    // Check if toast already exists
    const existingToast = document.getElementById('warpway-phantom-toast');
    if (existingToast) {
      existingToast.remove();
    }

    const toast = document.createElement('div');
    toast.id = 'warpway-phantom-toast';
    toast.className = 'warpway-phantom-toast';
    toast.innerHTML = `
      <div style="
        position: fixed;
        bottom: 80px;
        right: 20px;
        background: linear-gradient(135deg, #10b981 0%, #06b6d4 100%);
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 20px rgba(16, 185, 129, 0.4);
        z-index: 2147483646;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        font-size: 14px;
        font-weight: 600;
        animation: warpway-toast-slide-in 0.3s ease-out;
      ">
        👻 Phantom click recorded
      </div>
    `;

    // Add animation
    const animStyle = document.createElement('style');
    animStyle.textContent = `
      @keyframes warpway-toast-slide-in {
        from {
          transform: translateX(400px);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(animStyle);

    document.body.appendChild(toast);

    // Remove after 2 seconds
    setTimeout(() => {
      toast.remove();
      animStyle.remove();
    }, 2000);
  }

  /**
   * Generate selectors for an element using preference stack
   */
  private generateSelectors(element: HTMLElement): SelectorPreference[] {
    const selectors: SelectorPreference[] = [];

    // 1. Check for data-warp-* attributes (highest priority)
    const warpAttr = this.getWarpAttribute(element);
    if (warpAttr) {
      selectors.push({
        type: 'data-warp',
        selector: `[${warpAttr.name}="${warpAttr.value}"]`,
        confidence: 100,
      });
    }

    // 2. Check for data-testid
    const testId = element.getAttribute('data-testid');
    if (testId) {
      selectors.push({
        type: 'data-testid',
        selector: `[data-testid="${testId}"]`,
        confidence: 90,
      });
    }

    // 3. Check for ID attribute
    const id = element.getAttribute('id');
    if (id) {
      // Check for duplicate IDs on the page
      const duplicateCount = document.querySelectorAll(`#${id}`).length;
      if (duplicateCount > 1) {
        console.warn(
          `[Warpway Recorder] ⚠️  Duplicate ID detected: "${id}" appears ${duplicateCount} times on the page. ` +
          `This will cause selector ambiguity and may lead to incorrect element targeting during execution. ` +
          `Please ensure IDs are unique across the page.`
        );
        // Lower confidence due to ambiguity
        selectors.push({
          type: 'data-testid',
          selector: `#${id}`,
          confidence: 40, // Much lower confidence due to duplicate
          warning: `Duplicate ID: "${id}" appears ${duplicateCount} times`,
        });
      } else {
        selectors.push({
          type: 'data-testid', // Use same type for consistency
          selector: `#${id}`,
          confidence: 85,
        });
      }
    }

    // 4. Check for name attribute (common on form inputs)
    const name = element.getAttribute('name');
    if (name) {
      const tagName = element.tagName.toLowerCase();
      selectors.push({
        type: 'data-testid',
        selector: `${tagName}[name="${name}"]`,
        confidence: 80,
      });
    }

    // 5. Check for ARIA attributes
    const ariaSelector = this.generateAriaSelector(element);
    if (ariaSelector) {
      selectors.push({
        type: 'aria',
        selector: ariaSelector,
        confidence: 70,
      });
    }

    // 6. Generate text-based selector (lowest priority)
    const textSelector = this.generateTextSelector(element);
    if (textSelector) {
      selectors.push({
        type: 'text',
        selector: textSelector,
        confidence: 50,
      });
    }

    // 7. Fallback: class-based selector if nothing else works
    if (selectors.length === 0) {
      const className = element.className;
      if (className && typeof className === 'string' && className.trim()) {
        const tagName = element.tagName.toLowerCase();
        const firstClass = className.trim().split(/\s+/)[0];
        selectors.push({
          type: 'text',
          selector: `${tagName}.${firstClass}`,
          confidence: 30,
        });
      }
    }

    return selectors;
  }

  /**
   * Get data-warp-* attribute if present
   */
  private getWarpAttribute(element: HTMLElement): { name: string; value: string } | null {
    for (const attr of Array.from(element.attributes)) {
      if (attr.name.startsWith('data-warp-')) {
        return { name: attr.name, value: attr.value };
      }
    }
    return null;
  }

  /**
   * Generate ARIA-based selector
   */
  private generateAriaSelector(element: HTMLElement): string | null {
    const role = element.getAttribute('role');
    const label = element.getAttribute('aria-label');
    const labelledBy = element.getAttribute('aria-labelledby');

    if (role && label) {
      return `[role="${role}"][aria-label="${label}"]`;
    } else if (role) {
      return `[role="${role}"]`;
    } else if (label) {
      return `[aria-label="${label}"]`;
    } else if (labelledBy) {
      return `[aria-labelledby="${labelledBy}"]`;
    }

    return null;
  }

  /**
   * Generate text-based selector
   */
  private generateTextSelector(element: HTMLElement): string | null {
    const text = this.getElementText(element);
    if (!text || text.length > 50) return null;

    const tagName = element.tagName.toLowerCase();
    return `${tagName}:has-text("${text}")`;
  }

  /**
   * Get element text content (trimmed)
   */
  private getElementText(element: HTMLElement): string {
    const text = element.textContent || '';
    return text.trim().substring(0, 100);
  }

  /**
   * Get relevant attributes for metadata
   */
  private getRelevantAttributes(element: HTMLElement): Record<string, string> {
    const attrs: Record<string, string> = {};
    const relevantAttrs = ['id', 'name', 'type', 'placeholder', 'class'];

    for (const attrName of relevantAttrs) {
      const value = element.getAttribute(attrName);
      if (value) {
        attrs[attrName] = value;
      }
    }

    return attrs;
  }

  /**
   * Check if field should be masked for security
   */
  private isSensitiveField(element: HTMLElement): boolean {
    if (!(element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement)) {
      return false;
    }

    // Check input type
    if (element instanceof HTMLInputElement) {
      const sensitiveTypes = ['password', 'tel'];
      if (sensitiveTypes.includes(element.type)) {
        return true;
      }
    }

    // Check for sensitive patterns in name, id, placeholder, or autocomplete attributes
    const sensitivePatterns = [
      /password/i,
      /passwd/i,
      /pwd/i,
      /secret/i,
      /token/i,
      /otp/i,
      /pin/i,
      /ssn/i,
      /social.?security/i,
      /credit.?card/i,
      /card.?number/i,
      /cvv/i,
      /cvc/i,
      /security.?code/i,
      /routing.?number/i,
      /account.?number/i,
      /api.?key/i,
    ];

    const attributesToCheck = [
      element.getAttribute('name'),
      element.getAttribute('id'),
      element.getAttribute('placeholder'),
      element.getAttribute('autocomplete'),
      element.getAttribute('aria-label'),
    ];

    for (const attr of attributesToCheck) {
      if (attr) {
        for (const pattern of sensitivePatterns) {
          if (pattern.test(attr)) {
            return true;
          }
        }
      }
    }

    return false;
  }

  /**
   * Check if element is part of Warpway UI
   */
  private isWarpwayElement(element: HTMLElement): boolean {
    let current: HTMLElement | null = element;
    while (current) {
      if (
        current.className &&
        typeof current.className === 'string' &&
        current.className.includes('warpway-')
      ) {
        return true;
      }
      current = current.parentElement;
    }
    return false;
  }

  /**
   * Generate page signature (array of important DOM elements)
   */
  private generatePageSignature(): string[] {
    const signature: string[] = [];

    // Add h1-h3 headings
    document.querySelectorAll('h1, h2, h3').forEach((el) => {
      const text = el.textContent?.trim();
      if (text) {
        signature.push(`${el.tagName}:${text.substring(0, 50)}`);
      }
    });

    // Add form elements count
    const forms = document.querySelectorAll('form');
    if (forms.length > 0) {
      signature.push(`forms:${forms.length}`);
    }

    // Add buttons count
    const buttons = document.querySelectorAll('button');
    if (buttons.length > 0) {
      signature.push(`buttons:${buttons.length}`);
    }

    return signature.slice(0, 10); // Limit to first 10 elements
  }

  /**
   * Extract route pattern from URL (remove IDs, query params)
   */
  private extractRoutePattern(url: string): string {
    try {
      const urlObj = new URL(url);
      let path = urlObj.pathname;

      // Replace numeric IDs with :id
      path = path.replace(/\/\d+/g, '/:id');

      // Replace UUIDs with :uuid
      path = path.replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:uuid');

      return path;
    } catch {
      return url;
    }
  }

  /**
   * Generate unique step ID
   */
  private generateStepId(): string {
    return `step-${++this.stepCounter}-${Date.now()}`;
  }

  /**
   * Manually add a waitFor step
   */
  addWaitForStep(selector: string): void {
    if (!this.session) return;

    const step: RecordedStep = {
      id: this.generateStepId(),
      type: 'waitFor',
      timestamp: Date.now(),
      selectors: [
        {
          type: 'text',
          selector,
          confidence: 100,
        },
      ],
    };

    this.recordStep(step);
  }

  /**
   * Manually add an assert:text step
   */
  addAssertTextStep(text: string): void {
    if (!this.session) return;

    const step: RecordedStep = {
      id: this.generateStepId(),
      type: 'assert:text',
      timestamp: Date.now(),
      value: text,
    };

    this.recordStep(step);
  }

  /**
   * Manually add a refresh step
   */
  addRefreshStep(): void {
    if (!this.session) return;

    const step: RecordedStep = {
      id: this.generateStepId(),
      type: 'refresh',
      timestamp: Date.now(),
      url: this.stripWarpwayParam(window.location.href),
    };

    this.recordStep(step);
  }

  /**
   * Start periodic sync of steps to backend
   */
  private startPeriodicSync(): void {
    this.syncInterval = window.setInterval(() => {
      if (this.unsentSteps.length > 0) {
        this.syncStepsToBackend().catch((error) => {
          console.error('[Warpway Recorder] Periodic sync failed:', error);
          // Continue recording even if sync fails
        });
      }
    }, this.SYNC_INTERVAL_MS);
  }

  /**
   * Stop periodic sync
   */
  private stopPeriodicSync(): void {
    if (this.syncInterval !== null) {
      window.clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  /**
   * Sync unsent steps to backend
   */
  async syncStepsToBackend(): Promise<void> {
    // Prevent concurrent syncs
    if (this.isSyncing || this.unsentSteps.length === 0) {
      return;
    }

    // Must have a session with recording ID
    if (!this.session || !this.session.id) {
      console.warn('[Warpway Recorder] Cannot sync: no recording ID');
      return;
    }

    // Get API key
    const apiKey = getApiKey();
    if (!apiKey) {
      console.error('[Warpway Recorder] Cannot sync: no API key found');
      throw new Error('API key not found');
    }

    this.isSyncing = true;

    // Copy steps to sync and clear unsent queue
    const stepsToSync = [...this.unsentSteps];
    this.unsentSteps = [];

    try {
      await appendRecordingSteps(apiKey, this.session.id, stepsToSync);
      console.log(`[Warpway Recorder] Synced ${stepsToSync.length} steps to backend`);
    } catch (error) {
      console.error('[Warpway Recorder] Failed to sync steps:', error);
      // Re-add steps to unsent queue (prepend to maintain order)
      this.unsentSteps = [...stepsToSync, ...this.unsentSteps];
      throw error;
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Get count of unsent steps
   */
  getUnsentStepCount(): number {
    return this.unsentSteps.length;
  }

  /**
   * Check if currently syncing
   */
  isSyncingSteps(): boolean {
    return this.isSyncing;
  }

  /**
   * Enable/disable phantom click mode
   */
  setPhantomClickMode(enabled: boolean): void {
    this.phantomClickEnabled = enabled;
    if (this.onPhantomModeChange) {
      this.onPhantomModeChange(enabled);
    }
  }

  /**
   * Toggle phantom click mode
   */
  togglePhantomClickMode(): void {
    this.setPhantomClickMode(!this.phantomClickEnabled);
  }

  /**
   * Check if phantom click mode is enabled
   */
  isPhantomClickMode(): boolean {
    return this.phantomClickEnabled;
  }
}
